// The operations dashboard (screen 12), as admin_dashboard() returns it.
// Coaching only: the dashboard counts practice, and where it names a person it is so
// the training lead can plan a coaching conversation. It never ranks anyone.

import { skillName } from "./scenario";

export interface Count {
  done: number;
  assigned: number;
}

export interface CoachingPerson {
  staffId: string;
  name: string;
  phone: string;
  role: string;
  site: string;
  signedIn: boolean;
  done: number;
  assigned: number;
  lastWeek: Count;
  recent: { tries: number; bestFirst: number };
  weakSkill: string | null;
  missedScenario: { scenarioId: string; title: Record<string, string> } | null;
}

export interface Dashboard {
  period: "week" | "month";
  from: string;
  weekStart: string;
  serverTime: number;
  sample: boolean;
  practices: Count;
  people: { done: number; total: number };
  bestFirst: { people: number; tries: number; best: number } | null;
  sites: { siteId: string; name: string; people: { done: number; total: number }; practices: Count }[];
  hardest: { scenarioId: string; title: Record<string, string>; people: number; missedFirst: number }[];
  coaching: CoachingPerson[];
}

export const percent = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export const englishTitle = (title: Record<string, string>, fallback: string) =>
  title.en || title.mr || title.hi || fallback;

/** Why someone is on the coaching list, in plain words. */
export function reasons(p: CoachingPerson): string[] {
  const out: string[] = [];
  if (!p.signedIn && p.recent.tries === 0) out.push("Has not signed in yet");
  if (p.lastWeek.assigned > p.lastWeek.done) {
    out.push(`Finished ${p.lastWeek.done} of ${p.lastWeek.assigned} last week`);
  }
  if (p.recent.tries >= 2 && p.recent.bestFirst * 2 < p.recent.tries) {
    out.push("Often missed the best answer at the first choice lately");
  }
  return out;
}

/** A suggested next step for the coaching conversation. Fixed rules, no AI. */
export function nextStep(p: CoachingPerson): string {
  if (!p.signedIn && p.recent.tries === 0) return "Help them sign in on their phone for the first time.";
  if (p.recent.tries >= 2 && p.recent.bestFirst * 2 < p.recent.tries && p.missedScenario) {
    const title = englishTitle(p.missedScenario.title, p.missedScenario.scenarioId);
    return `Go through "${title}" together and talk about the rule to remember.`;
  }
  if (p.lastWeek.assigned > p.lastWeek.done) {
    return "Ask how practice is going and help them find ten minutes in their shift.";
  }
  return p.weakSkill ? `Practise ${skillName(p.weakSkill).toLowerCase()} together.` : "Check in with them.";
}
